import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "@/lib/router-compat";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { Navbar } from "@/components/public/Navbar";
import { PropertyGrid } from "@/components/public/PropertyGrid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePageSEO } from "@/hooks/usePageSEO";
import { formatPriceDisplay } from "@/utils/currency";
import { MapPin, Building, TrendingUp, Home, IndianRupee, Phone, CheckCircle2, Info, HelpCircle } from "lucide-react";

interface LocalityStats {
  totalProperties: number;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  propertyTypes: string[];
  avgArea: number;
}

export default function LocalityLanding() {
  const { locality } = useParams<{ locality: string }>();
  const [stats, setStats] = useState<LocalityStats | null>(null);
  const [loading, setLoading] = useState(true);

  // Convert slug back to readable name
  const localityName = useMemo(() => {
    if (!locality) return "";
    return locality
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [locality]);

  usePageSEO({
    title: `Properties in ${localityName}, Mumbai | MNRE Real Estate`,
    description: `Browse ${localityName} properties for sale — apartments, villas & commercial spaces. View prices, amenities & book viewings with MNRE.`,
    canonicalPath: `/properties-in/${locality}`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "RealEstateAgent",
      name: "MNRE - Mohnish Nagpal Real Estate",
      areaServed: {
        "@type": "Place",
        name: `${localityName}, Mumbai`,
      },
      url: `${typeof window !== "undefined" ? window.location.origin : ""}/properties-in/${locality}`,
    },
  });

  useEffect(() => {
    const fetchStats = async () => {
      if (!localityName) return;
      setLoading(true);

      const { data } = await supabase
        .from("properties")
        .select("price, area_sqft, property_type")
        .eq("is_published", true)
        .or(`locality.ilike.%${localityName}%,city.ilike.%${localityName}%`);

      if (data && data.length > 0) {
        const prices = data.filter((p) => p.price).map((p) => p.price!);
        const areas = data.filter((p) => p.area_sqft).map((p) => p.area_sqft!);
        const types = [...new Set(data.map((p) => p.property_type))];

        setStats({
          totalProperties: data.length,
          avgPrice: prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : 0,
          minPrice: prices.length ? Math.min(...prices) : 0,
          maxPrice: prices.length ? Math.max(...prices) : 0,
          propertyTypes: types,
          avgArea: areas.length ? Math.round(areas.reduce((a, b) => a + b, 0) / areas.length) : 0,
        });
      } else {
        setStats({
          totalProperties: 0,
          avgPrice: 0,
          minPrice: 0,
          maxPrice: 0,
          propertyTypes: [],
          avgArea: 0,
        });
      }
      setLoading(false);
    };
    fetchStats();
  }, [localityName]);

  const searchFilters = useMemo(
    () => ({ location: localityName } as const),
    [localityName]
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary/10 via-background to-secondary/10 py-16 md:py-24 border-b">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <Badge variant="secondary" className="mb-4">
              <MapPin className="h-3 w-3 mr-1" /> Mumbai
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold font-playfair italic mb-4">
              Properties in {localityName}
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Discover premium real estate in {localityName}, Mumbai. Browse verified listings,
              compare prices, and find your perfect property with MNRE.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact">
                <Button size="lg">
                  <Phone className="h-4 w-4 mr-2" /> Get Expert Advice
                </Button>
              </Link>
              <Link to="/properties">
                <Button variant="outline" size="lg">
                  View All Properties
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="container mx-auto px-4 -mt-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-5">
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Card>
                <CardContent className="p-5 text-center">
                  <Building className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">{stats?.totalProperties || 0}</div>
                  <div className="text-sm text-muted-foreground">Available Properties</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5 text-center">
                  <IndianRupee className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">
                    {stats?.avgPrice
                      ? formatPriceDisplay({ price: stats.avgPrice, price_min: null, price_max: null })
                      : "N/A"}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg. Price</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5 text-center">
                  <Home className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">{stats?.propertyTypes.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Property Types</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-5 text-center">
                  <TrendingUp className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="text-2xl font-bold">
                    {stats?.avgArea ? `${stats.avgArea} sqft` : "N/A"}
                  </div>
                  <div className="text-sm text-muted-foreground">Avg. Area</div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </section>

      {/* Property types available */}
      {stats && stats.propertyTypes.length > 0 && (
        <section className="container mx-auto px-4 py-8">
          <h2 className="text-xl font-semibold mb-4">Property Types in {localityName}</h2>
          <div className="flex flex-wrap gap-2">
            {stats.propertyTypes.map((type) => (
              <Link key={type} to={`/properties?type=${type}`}>
                <Badge variant="outline" className="capitalize cursor-pointer hover:bg-primary/10">
                  {type}
                </Badge>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Property Listings */}
      <section className="container mx-auto px-4 pb-16">
        <h2 className="text-2xl font-bold mb-6">
          All Properties in {localityName}
        </h2>
        <PropertyGrid searchFilters={searchFilters} />
      </section>

      {/* Locality Market Intelligence & AI-Search Questions */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-4xl mx-auto space-y-8">
          <div>
            <Badge variant="outline" className="mb-2">
              <Info className="h-3.5 w-3.5 mr-1" /> Locality Market Intelligence
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold font-playfair italic">
              Buying Property in {localityName}: Key Facts & Insights
            </h2>
            <p className="text-muted-foreground mt-2">
              Objective market data, verified government rates, and practical guidance for buyers and investors in {localityName}, Mumbai.
            </p>
          </div>

          {/* Verified Data vs Estimates Card */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl border bg-card space-y-3">
              <div className="flex items-center gap-2 font-semibold text-primary">
                <CheckCircle2 className="h-4 w-4" />
                <span>Verified Regulatory Benchmarks</span>
              </div>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>• <strong>Stamp Duty:</strong> 6% of Agreement Value (5% base + 1% Metro Cess).</li>
                <li>• <strong>Registration:</strong> 1% capped at ₹30,000 max for residential properties.</li>
                <li>• <strong>GST:</strong> 0% on ready properties with OC; 5% on under-construction units.</li>
                <li>• <strong>Area Standard:</strong> All transactions governed strictly by MahaRERA Carpet Area.</li>
              </ul>
            </div>

            <div className="p-5 rounded-xl border bg-card space-y-3">
              <div className="flex items-center gap-2 font-semibold text-foreground">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span>Market Estimates & Practical Averages</span>
              </div>
              <ul className="text-sm space-y-2 text-muted-foreground">
                <li>• <strong>Average Price:</strong> {stats?.avgPrice ? formatPriceDisplay({ price: stats.avgPrice, price_min: null, price_max: null }) : "Available upon request"}.</li>
                <li>• <strong>Gross Rental Yield:</strong> Typically 2.5% to 3.4% annually for residential.</li>
                <li>• <strong>Resale Negotiation:</strong> 2% to 4% margin typically observed in secondary market.</li>
                <li>• <strong>Parking Allotment:</strong> Stilt / podium parking rights valued at ₹10L–₹25L depending on project.</li>
              </ul>
            </div>
          </div>

          {/* Frequently Asked Questions by Buyers & Investors */}
          <div className="pt-4">
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle className="h-5 w-5 text-primary" />
              <h3 className="text-xl font-bold font-playfair italic">
                Frequently Asked Questions About {localityName}
              </h3>
            </div>
            <Accordion type="single" collapsible className="w-full space-y-3">
              <AccordionItem value="faq-1" className="border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-left font-medium py-3">
                  What is the average price to buy a flat in {localityName}?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-4">
                  Property prices in {localityName} depend heavily on the specific avenue, building age, and developer profile. Across active inventory, 2 BHK apartments typically range between ₹3.2 Crore and ₹5.5 Crore, while premium 3 BHK residences range from ₹6.0 Crore to ₹11.5 Crore+.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-2" className="border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-left font-medium py-3">
                  Is {localityName} a good choice for real estate investment?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-4">
                  Yes. {localityName} benefits from enduring tenant demand from senior corporate executives, healthcare professionals, and entrepreneurs. While gross rental yields average 2.5% to 3.2%, capital appreciation is bolstered by ongoing high-quality society redevelopments and infrastructure upgrades like the Mumbai Coastal Road and Metro network.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="faq-3" className="border rounded-lg px-4 bg-card">
                <AccordionTrigger className="text-left font-medium py-3">
                  What additional costs must be budgeted when buying in {localityName}?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed pt-1 pb-4">
                  In Mumbai, buyers should budget roughly 8% to 11% above the base agreement value for mandatory transaction costs: 6% Maharashtra Stamp Duty (including 1% Metro Cess), ₹30,000 Registration fee, society share capital and transfer fees, and 5% GST if buying an under-construction unit (0% GST if the building has an Occupancy Certificate).
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-primary to-primary/80 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-4">
            Looking for a Property in {localityName}?
          </h2>
          <p className="text-primary-foreground/90 mb-6 max-w-xl mx-auto">
            Our experts have deep knowledge of {localityName}. Get personalized property
            recommendations and exclusive deals.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="lg">
                <Phone className="h-4 w-4 mr-2" /> Talk to an Expert
              </Button>
            </Link>
            <a href="https://wa.me/917021011304" target="_blank" rel="noopener noreferrer">
              <Button variant="secondary" size="lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
